import React from 'react';

export type VideoModel = 'auto' | 'veo-2' | 'veo-3' | 'veo-3-fast';
export type VideoProvider = 'auto' | 'vertex' | 'gemini';

interface VideoModelOption {
  value: VideoModel;
  label: string;
  icon: string;
  description: string;
  features: string[];
  pricing?: { vertex?: number; gemini?: number };
  availability: string;
}

const modelOptions: VideoModelOption[] = [
  {
    value: 'auto',
    label: '자동 선택',
    icon: '🤖',
    description: '사용 가능한 최고의 모델 자동 선택',
    features: ['스마트 선택', '자동 폴백'],
    availability: '모든 API'
  },
  {
    value: 'veo-3',
    label: 'Veo 3 (최신)',
    icon: '🎬',
    description: '오디오 포함 고품질 8초 영상',
    features: ['오디오 생성', '1080p HD', '8초', '최고 품질'],
    pricing: { vertex: 0.45, gemini: 0.40 },
    availability: 'Vertex AI & Gemini API (Paid)'
  },
  {
    value: 'veo-3-fast',
    label: 'Veo 3 Fast',
    icon: '⚡',
    description: '빠른 생성 속도 최적화',
    features: ['빠른 생성', '1080p HD', '8초'],
    pricing: { vertex: 0.25, gemini: 0.20 },
    availability: 'Vertex AI & Gemini API (Paid)'
  },
  {
    value: 'veo-2',
    label: 'Veo 2 (안정)',
    icon: '🎥',
    description: '검증된 안정적인 영상 생성',
    features: ['8초', '720p', '안정적'],
    pricing: { vertex: 0.40, gemini: 0.35 },
    availability: 'Vertex AI & Gemini API (Paid)'
  },
];

interface VideoModelSelectorProps {
  value: VideoModel;
  onChange: (model: VideoModel) => void;
  disabled?: boolean;
}

export const VideoModelSelector: React.FC<VideoModelSelectorProps> = ({
  value,
  onChange,
  disabled = false
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const selectedOption = modelOptions.find(opt => opt.value === value) || modelOptions[0];

  return (
    <div className="mb-6">
      <label className="block text-sm font-medium text-slate-300 mb-2">
        🎬 비디오 생성 모델
      </label>
      
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          disabled={disabled}
          className={`w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg 
            text-left flex items-center justify-between transition-all
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-slate-700 cursor-pointer'}
            ${isExpanded ? 'ring-2 ring-indigo-500' : ''}`}
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">{selectedOption.icon}</span>
            <div>
              <div className="text-white font-medium">{selectedOption.label}</div>
              <div className="text-xs text-slate-400">{selectedOption.description}</div>
            </div>
          </div>
          <svg 
            className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isExpanded && !disabled && (
          <div className="absolute z-10 w-full mt-2 bg-slate-800 border border-slate-600 rounded-lg shadow-xl overflow-hidden">
            {modelOptions.map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsExpanded(false);
                }}
                className={`w-full px-4 py-3 text-left hover:bg-slate-700 transition-all
                  ${option.value === value ? 'bg-slate-700' : ''}`}
              >
                <div className="flex items-start gap-3">
                  <span className="text-2xl mt-1">{option.icon}</span>
                  <div className="flex-1">
                    <div className="text-white font-medium">{option.label}</div>
                    <div className="text-sm text-slate-400 mt-1">{option.description}</div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {option.features.map(feature => (
                        <span 
                          key={feature}
                          className="text-xs px-2 py-1 bg-slate-900 text-slate-300 rounded"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                    {option.requirement && (
                      <div className="text-xs text-amber-400 mt-2">
                        ⚠️ {option.requirement} 필요
                      </div>
                    )}
                  </div>
                  {option.value === value && (
                    <svg className="w-5 h-5 text-indigo-400 mt-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Model info display */}
      <div className="mt-3 p-3 bg-slate-900/50 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="text-xs text-slate-400">
            현재 선택: <span className="text-slate-300 font-medium">{selectedOption.label}</span>
          </div>
          {selectedOption.requirement && (
            <div className="text-xs text-amber-400">
              {selectedOption.requirement}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};